import { useEffect, useState, useMemo } from 'react'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useModals } from 'almanac-ui/Modal'
import { VisualStylesModal } from 'components/VisualStylesModal'
import Styles from './BrandVisualStylePreview.module.scss'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Check02 } from 'blaze-ui/icons/16'
import { useStyledImage } from 'components/VisualStylePreviewCarousel/useStyledImage'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { StyleImageLoader } from '../BrandVisualStyleSelection/StyleImageLoader'
import { useVisualStyles } from 'hooks/queries/visualStyles'

export interface BrandVisualStylePreviewProps {
  onNext: (selectedVisualStyle: StyleGenerated, stylesGenerated: StyleGenerated[]) => void
  onBack: () => void
  initialStylesGenerated?: StyleGenerated[]
  initialSelectedStyle?: StyleGenerated
  sourceImage: MediaFile | null
  brandKitId?: number
}

const FAKE_DURATIONS = [65000, 85000, 75000]
const DEFAULT_STYLES = ['Morning Contrast', 'Sunset Studio', 'Black and White Minimal']

export const BrandVisualStylePreview = ({
  onNext,
  onBack,
  initialStylesGenerated = [],
  initialSelectedStyle,
  sourceImage,
  brandKitId,
}: BrandVisualStylePreviewProps) => {
  const { openModal } = useModals()
  const { data: visualStyles } = useVisualStyles()

  const [stylesGenerated, setStylesGenerated] = useState<StyleGenerated[]>(sourceImage ? [] : initialStylesGenerated)
  const [completionState, setCompletionState] = useState<boolean[]>([false, false, false])
  const [selectedVisualStyleId, setSelectedVisualStyleId] = useState<number | null>(
    initialSelectedStyle?.id || stylesGenerated[0]?.id,
  )
  const [generationDone, setGenerationDone] = useState<boolean>(false)
  const [newStyleId, setNewStyleId] = useState<number | null>(null)

  const displayedVisualStyles = useMemo(() => visualStyles?.slice(0, 3) || [], [visualStyles])
  const allComplete = useMemo(() => completionState.every(Boolean), [completionState])

  const styled1 = useStyledImage(sourceImage || undefined, displayedVisualStyles[0]?.id ?? null, brandKitId)
  const styled2 = useStyledImage(sourceImage || undefined, displayedVisualStyles[1]?.id ?? null, brandKitId)
  const styled3 = useStyledImage(sourceImage || undefined, displayedVisualStyles[2]?.id ?? null, brandKitId)
  const styledImages = useMemo(() => [styled1, styled2, styled3], [styled1, styled2, styled3])

  const newVisualStyle = useMemo(
    () => (newStyleId && visualStyles?.find((style) => style.id === newStyleId)) || null,
    [newStyleId, visualStyles],
  )
  const newStyle = useStyledImage(sourceImage || undefined, newStyleId, brandKitId, true)

  const markComplete = (index: number) => {
    setCompletionState((prev) => {
      const next = [...prev]
      next[index] = true
      return next
    })
  }

  useEffect(() => {
    if (!sourceImage || displayedVisualStyles.length !== 3 || !allComplete || generationDone) return

    setGenerationDone(true)
    const generatedStyles = displayedVisualStyles.map((style, index) => ({
      styleUrl: styledImages[index].styledUrl || '',
      ...style,
    }))
    setStylesGenerated(generatedStyles)
    setSelectedVisualStyleId(displayedVisualStyles[0]?.id)
  }, [sourceImage, allComplete, generationDone, displayedVisualStyles, styledImages])

  useEffect(() => {
    if (stylesGenerated.length || sourceImage || !visualStyles) return

    const defaultStyles = DEFAULT_STYLES.map((name) => visualStyles.find((style) => style.name === name)).filter(
      (style): style is NonNullable<typeof style> => style !== undefined,
    )

    if (defaultStyles.length === 3) {
      setSelectedVisualStyleId(defaultStyles[0].id)
      setStylesGenerated(
        defaultStyles.map((style) => ({
          ...style,
          styleUrl: style.cover_image_url,
        })),
      )
    }
  }, [stylesGenerated.length, sourceImage, visualStyles])

  useEffect(() => {
    if (!newStyle.styledUrl || newStyle.isLoading || stylesGenerated.find((s) => s.id === newStyleId)) return

    setStylesGenerated([
      stylesGenerated[0],
      {
        id: newStyleId || 0,
        styleUrl: newStyle.styledUrl || '',
        name: newVisualStyle?.name || '',
        cover_image_url: newVisualStyle?.cover_image_url || '',
      },
      ...stylesGenerated.slice(1),
    ])
  }, [newStyle.styledUrl, newStyle.isLoading, stylesGenerated, newStyleId, newVisualStyle])

  useEffect(() => {
    if (!sourceImage) return
    setCompletionState([false, false, false])
    setGenerationDone(false)
  }, [sourceImage])

  const handleStyleSelection = (styleId: number) => {
    const existingIndex = stylesGenerated.findIndex((s) => s.id === styleId)

    if (existingIndex !== -1) {
      if (existingIndex > 2) {
        setStylesGenerated([
          stylesGenerated[0],
          stylesGenerated[existingIndex],
          ...stylesGenerated.slice(1, existingIndex),
          ...stylesGenerated.slice(existingIndex + 1),
        ])
      }
      setSelectedVisualStyleId(styleId)
      return
    }

    if (!sourceImage) {
      const newStyleToAdd = visualStyles?.find((s) => s.id === styleId)
      if (newStyleToAdd) {
        setStylesGenerated([
          stylesGenerated[0],
          { ...newStyleToAdd, styleUrl: newStyleToAdd.cover_image_url },
          ...stylesGenerated.slice(1),
        ])
        setSelectedVisualStyleId(styleId)
      }
      return
    }

    setSelectedVisualStyleId(styleId)
    setNewStyleId(styleId)
    newStyle.triggerGeneration()
  }

  const handleNext = () => {
    if (!selectedVisualStyleId) return

    const selectedStyle = stylesGenerated.find((s) => s.id === selectedVisualStyleId) || {
      id: selectedVisualStyleId,
      styleUrl: '',
      name: '',
      cover_image_url: '',
    }

    onNext(selectedStyle, stylesGenerated)
  }

  const isButtonDisabled = newStyle.isLoading || (!!sourceImage && !generationDone)

  return (
    <MultiStepStepLayout
      title="Select a Visual Style for your first batch of content"
      description="We'll apply this style to all generated images to keep your content visually consistent. You can change this anytime later!"
      scrollContainerClassName={Styles.scrollContainer}
      contentClassName={Styles.customContent}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <div className={Styles.footerSide}>
                <Button variant="tertiary" size="lg" onPress={onBack}>
                  Back
                </Button>
              </div>
              <div className={Styles.footerCenter}>
                <Button
                  size="lg"
                  variant="secondary"
                  onPress={() =>
                    openModal(VisualStylesModal, {
                      selectedStyleId: selectedVisualStyleId,
                      onConfirm: handleStyleSelection,
                    })
                  }
                  isDisabled={isButtonDisabled}
                  color="var(--dark-80)"
                >
                  Browse All Styles
                </Button>
              </div>
              <div className={Styles.footerSide}>
                <Button size="lg" onPress={handleNext} isDisabled={isButtonDisabled}>
                  Next, pick your brand fonts!
                </Button>
              </div>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.stylesContainer}>
          {sourceImage
            ? displayedVisualStyles.map((style, index) => (
                <div
                  className={Styles.styleContainer}
                  key={style.id}
                  onClick={generationDone ? () => setSelectedVisualStyleId(style.id) : undefined}
                  style={{ cursor: generationDone ? 'pointer' : 'default' }}
                >
                  {generationDone && selectedVisualStyleId === style.id && (
                    <div className={Styles.selectedCheck}>
                      <Check02 size={15} color="var(--dark-80)" />
                    </div>
                  )}

                  <StyleImageLoader
                    sourceImage={sourceImage}
                    stylesGeneration={styledImages[index]}
                    onTransformedImageLoaded={() => markComplete(index)}
                    withTag={false}
                    fakeDuration={FAKE_DURATIONS[index]}
                  />

                  {generationDone && (
                    <>
                      <div className={Styles.styleNameContainer}>
                        <div className={Styles.styleName}>
                          {selectedVisualStyleId === style.id && (
                            <div className={Styles.checkmarkCircle}>
                              <Check02 size={12} color="var(--dark-80)" />
                            </div>
                          )}
                          <Heading color="var(--light-100)" level={3}>
                            {style.name}
                          </Heading>
                        </div>
                      </div>
                      {index === 0 && <div className={Styles.recommendedTag}>Recommended</div>}
                    </>
                  )}
                </div>
              ))
            : stylesGenerated.slice(0, 3).map((style, index) => (
                <div
                  className={Styles.styleContainer}
                  key={style.id}
                  onClick={() => setSelectedVisualStyleId(style.id)}
                >
                  {selectedVisualStyleId === style.id && (
                    <div className={Styles.selectedCheck}>
                      <Check02 size={15} color="var(--dark-80)" />
                    </div>
                  )}

                  {newStyle.isLoading && index === 1 && sourceImage ? (
                    <StyleImageLoader
                      sourceImage={sourceImage}
                      stylesGeneration={newStyle}
                      onTransformedImageLoaded={() => {}}
                      withTag={false}
                    />
                  ) : (
                    <StyleImageLoader staticMode staticImageUrl={style.styleUrl} withTag={false} />
                  )}

                  <div className={Styles.styleNameContainer}>
                    <div className={Styles.styleName}>
                      {selectedVisualStyleId === style.id && (
                        <div className={Styles.checkmarkCircle}>
                          <Check02 size={12} color="var(--dark-80)" />
                        </div>
                      )}
                      <Heading color="var(--light-100)" level={3}>
                        {newStyle.isLoading && index === 1 ? newVisualStyle?.name : style.name}
                      </Heading>
                    </div>
                  </div>
                  {index === 0 && <div className={Styles.recommendedTag}>Recommended</div>}
                </div>
              ))}
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
